import OfferDocumentPanel, { OfferDocumentDraft } from '../components/OfferDocumentPanel';
import OfferRevisionCreate from '../components/OfferRevisionCreate';
import { useOfferRevisions } from '../hooks/useOfferRevisions';
import { History, Loader2, Save, Undo2 } from 'lucide-react';
import OfferRevisions from '../components/OfferRevisions';
import OfferExternalStatus from '../components/OfferExternalStatus';
import { DocumentTypes } from '../components/sales/SalesDocumentViews';
import { clientReturnUrl, dateLabel } from '../components/sales/documentTypes';
import OfferPromotionsPanel from '../components/OfferPromotionsPanel';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClient } from '@offertify/query';
import { offersAPI, templatesAPI, pricingAPI, wmsAPI, promozioniAPI } from '../utils/api';
import { CreateOffertaRequest, CreateOffertaItemRequest, PriceResolution, WmsConnectionStatus, ScontoViolazione, Promozione } from '../types';
import toast from 'react-hot-toast';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon, DocumentTextIcon, Cog6ToothIcon, ExclamationTriangleIcon, ShieldCheckIcon, CubeIcon, ArrowPathIcon, CalendarDaysIcon, Bars3Icon, PaperAirplaneIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import WmsOrderTracker from '../components/WmsOrderTracker';
import ProductAutocomplete, { ProductSelection } from '../components/ProductAutocomplete';
import AttivitaTab from '../components/attivita/AttivitaTab';
import OfferStatusBadge from '../components/OfferStatusBadge';
import ConfirmModal from '../components/ConfirmModal';
import ClientPicker from '../components/ClientPicker';
import { usePermissions } from '../hooks/usePermissions';

type TabType = 'details' | 'content' | 'attivita' | 'revisions';

const OfferEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [routeParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  // Save permission: creating a new offer requires offerte:create,
  // updating an existing one requires offerte:edit.
  const canSave = isEditing ? hasPermission('offerte:edit') : hasPermission('offerte:create');
  const canSendOfferState = hasPermission('offerte:send');
  const canApproveOffer = hasPermission('offerte:approve');
  const canDeleteOffer = hasPermission('offerte:delete');

  const [activeTab, setActiveTab] = useState<TabType>('details');
  const tabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (activeTab === 'content') tabsRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [activeTab]);
  const [configurationPinned, setConfigurationPinned] = useState(true);
  const [configurationOpen, setConfigurationOpen] = useState(true);
  const configurationRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (configurationPinned || !configurationOpen) return;
    const closeOutside = (event: MouseEvent) => {
      if (!configurationRef.current?.contains(event.target as Node)) setConfigurationOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [configurationPinned, configurationOpen]);

  const [documentDirty, setDocumentDirty] = useState(false);
  const [documentBusy, setDocumentBusy] = useState(false);
  const [documentReset, setDocumentReset] = useState(0);
  const documentDraft = useRef<OfferDocumentDraft | null>(null);
  const onDocumentDraft = useCallback((draft: OfferDocumentDraft) => {documentDraft.current = draft;}, []);
  const revisions = useOfferRevisions(id || '');
  const pricingSequence = useRef(0);
  const pricingClient = useRef<string | null>(null);
  const [formData, setFormData] = useState<CreateOffertaRequest>({
    cliente_id: !id ? routeParams.get('cliente_id') || '' : '',
    titolo: '',
    numero_custom: '',
    descrizione: '',
    markdown_content: '',
    html_content: '',
    importo_totale: 0,
    importo_imponibile: 0,
    importo_iva: 0,
    aliquota_iva: 22,
    scadenza: '',
    giorni_validita: 30,
    note: '',
    condizioni_pagamento: '',
    template_id: '',
    firma_digitale: false,
    items: [],
    promozione_ids: [],
  });

  const savedDocument = useRef<{offerId?: string; form: string; items: string; pricing: Record<number, PriceResolution>} | null>(!id ? {form:JSON.stringify(formData),items:'[]',pricing:{}} : null);
  const [items, setItems] = useState<CreateOffertaItemRequest[]>([]);
  const commercialDirtyRef = useRef(false);
  commercialDirtyRef.current = !!savedDocument.current && (savedDocument.current.form !== JSON.stringify(formData) || savedDocument.current.items !== JSON.stringify(items));
  // Track pricing resolutions per item index
  const [itemPricing, setItemPricing] = useState<Record<number, PriceResolution>>({});
  // Drag & drop reorder state (dragged row index + drop target row index)
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Anteprima violazioni sconto per l'utente corrente (venditore)
  const [scontoViolazioni, setScontoViolazioni] = useState<ScontoViolazione[]>([]);

  // Load offer data if editing
  const { data: offerData, isLoading: isLoadingOffer } = useQuery({
    queryKey: ['offer', id],
    queryFn: () => id ? offersAPI.getOffer(id) : Promise.resolve(null),
    enabled: !!id,
  });

  const { data: selectedClientData } = useClient(formData.cliente_id || undefined);

  // Load templates
  const { data: templatesData } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templatesAPI.getTemplates({ tipo: 'offerta', limit: 100 }),
  });

  // Promozioni attive selezionabili manualmente sull'offerta
  const { data: promozioniAttive, isLoading: promotionsLoading, isError: promotionsError, refetch: refetchPromotions } = useQuery({
    queryKey: ['promozioni-attive'],
    queryFn: async () => {
      const res = await promozioniAPI.getPromozioniAttive();
      return res.data as Promozione[];
    },
  });

  const templates = templatesData?.data?.data || [];
  const selectablePromozioni = [
    ...(promozioniAttive || []),
    ...(offerData?.data?.promozioni || []).filter((promo) =>
      (formData.promozione_ids || []).includes(promo.id) &&
      !(promozioniAttive || []).some((active) => active.id === promo.id)
    ),
  ];

  // WMS integration
  const { data: wmsStatus } = useQuery<WmsConnectionStatus>({
    queryKey: ['wms-status'],
    queryFn: async () => {
      const response = await wmsAPI.getStatus();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const offer = offerData?.data;
  const canEditOffer = canSave && (!isEditing || offer?.stato === 'bozza');
  const returnTo = clientReturnUrl(routeParams.get('returnTo'), offer?.cliente_id || formData.cliente_id) || '/offers';
  const isAccettata = offer?.stato === 'accettata';
  const hasWmsOrder = !!offer?.wms_order_id;

  const createWmsOrder = useMutation({
    mutationFn: (offertaId: string) => wmsAPI.createOrder(offertaId),
    onSuccess: () => {
      toast.success('Ordine magazzino creato');
      if (id) queryClient.invalidateQueries({ queryKey: ['offer', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione ordine WMS');
    },
  });

  const confirmWmsOrder = useMutation({
    mutationFn: (offertaId: string) => wmsAPI.confirmOrder(offertaId),
    onSuccess: () => {
      toast.success('Ordine confermato');
      if (id) queryClient.invalidateQueries({ queryKey: ['offer', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella conferma ordine');
    },
  });

  const syncWmsStatus = useMutation({
    mutationFn: (offertaId: string) => wmsAPI.syncOrderStatus(offertaId),
    onSuccess: () => {
      toast.success('Stato aggiornato');
      if (id) queryClient.invalidateQueries({ queryKey: ['offer', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento stato');
    },
  });

  const selectedClient = selectedClientData;

  // Update form data when offer is loaded
  useEffect(() => {
    if (offerData?.data) {
      const offer = offerData.data;
      if (commercialDirtyRef.current && savedDocument.current?.offerId === offer.id) return;
      pricingSequence.current += 1;
      pricingClient.current = offer.cliente_id;
      setItemPricing({});

      const loadedForm: CreateOffertaRequest = {
        cliente_id: offer.cliente_id,
        titolo: offer.titolo,
        numero_custom: offer.numero_custom || offer.numero,
        descrizione: offer.descrizione || '',
        markdown_content: offer.markdown_content || '',
        html_content: offer.html_content || '',
        section_values: offer.section_values || {},
        importo_totale: Number(offer.importo_totale),
        importo_imponibile: Number(offer.importo_imponibile),
        importo_iva: Number(offer.importo_iva),
        aliquota_iva: Number(offer.aliquota_iva),
        scadenza: offer.scadenza?.split('T')[0] || '',
        giorni_validita: offer.giorni_validita,
        note: offer.note || '',
        condizioni_pagamento: offer.condizioni_pagamento || '',
        template_id: offer.template_id || '',
        firma_digitale: offer.firma_digitale || false,
        items: [],
        promozione_ids: (offer.promozioni || []).map((p: any) => p.id),
      };
      setFormData(loadedForm);
      savedDocument.current = {offerId:offer.id, form: JSON.stringify(loadedForm), items: '[]', pricing:{}};
      setItems([]);
      // Load items from offer (convert string decimals to numbers)
      if (offer.items && offer.items.length > 0) {
        const loadedItems = offer.items.map((item: any) => ({
          descrizione: item.descrizione,
          quantita: Number(item.quantita),
          unita_misura: item.unita_misura || '',
          prezzo_unitario: Number(item.prezzo_unitario),
          sconto_percentuale: Number(item.sconto_percentuale || 0),
          aliquota_iva: item.aliquota_iva != null ? Number(item.aliquota_iva) : Number(offer.aliquota_iva),
          importo_totale: Number(item.importo_totale),
          ordine: item.ordine,
          prodotto_id: item.prodotto_id || undefined,
        }));
        setItems(loadedItems);
        savedDocument.current.items = JSON.stringify(loadedItems);

        // Restore pricing info from saved promozioni_applicate
        const savedPricing: Record<number, PriceResolution> = {};
        offer.items.forEach((item: any, idx: number) => {
          if (item.promozioni_applicate && item.promozioni_applicate.length > 0) {
            savedPricing[idx] = {
              prodotto_id: item.prodotto_id || '',
              prodotto_nome: item.descrizione,
              prezzo_base: Number(item.prezzo_unitario),
              prezzo_listino: item.prezzo_listino != null ? Number(item.prezzo_listino) : undefined,
              prezzo_finale: item.prezzo_promozione != null ? Number(item.prezzo_promozione) : Number(item.prezzo_unitario),
              promozioni_applicate: item.promozioni_applicate.map((p: any) => ({
                promozione_id: p.promozione_id,
                nome: p.nome,
                tipo: p.tipo,
                valore: Number(p.valore),
                prezzo_prima: Number(p.prezzo_prima),
                prezzo_dopo: Number(p.prezzo_dopo),
                impatta_prezzo: p.impatta_prezzo !== false,
              })),
            };
          } else if (item.prezzo_listino != null) {
            savedPricing[idx] = {
              prodotto_id: item.prodotto_id || '',
              prodotto_nome: item.descrizione,
              prezzo_base: Number(item.prezzo_unitario),
              prezzo_listino: Number(item.prezzo_listino),
              prezzo_finale: item.prezzo_promozione != null ? Number(item.prezzo_promozione) : Number(item.prezzo_unitario),
              promozioni_applicate: [],
            };
          }
        });
        if (Object.keys(savedPricing).length > 0) {
          savedDocument.current.pricing = JSON.parse(JSON.stringify(savedPricing));
          setItemPricing(savedPricing);
        }
      }
    }
  }, [offerData]);

  // Re-resolve pricing when client changes
  useEffect(() => {
    const sequence = ++pricingSequence.current;
    if (!formData.cliente_id || items.length === 0 || !canEditOffer) return;
    if (pricingClient.current === formData.cliente_id) return;
    pricingClient.current = formData.cliente_id;

    const prodottoIds = items
      .map(item => item.prodotto_id)
      .filter((id): id is string => !!id);

    if (prodottoIds.length === 0) return;

    const resolvePrices = async () => {
      try {
        const response = await pricingAPI.resolvePricesBatch(formData.cliente_id, prodottoIds);
        if (sequence !== pricingSequence.current) return;
        const resolutions = response.data.prezzi;

        const newPricing: Record<number, PriceResolution> = {};
        const updatedItems = [...items];
        let changed = false;

        items.forEach((item, idx) => {
          if (!item.prodotto_id) return;
          const resolution = resolutions.find(r => r.prodotto_id === item.prodotto_id);
          if (resolution) {
            newPricing[idx] = resolution;
            // Update price if it was from a previous pricing resolution (not manually set)
            const prevPricing = itemPricing[idx];
            if (prevPricing && Number(item.prezzo_unitario) === Number(prevPricing.prezzo_finale)) {
              updatedItems[idx] = { ...updatedItems[idx], prezzo_unitario: Number(resolution.prezzo_finale) };
              const subtotal = updatedItems[idx].quantita * Number(resolution.prezzo_finale);
              const discount = subtotal * ((updatedItems[idx].sconto_percentuale || 0) / 100);
              updatedItems[idx].importo_totale = subtotal - discount;
              changed = true;
            }
          }
        });

        setItemPricing(newPricing);
        if (changed) {
          setItems(updatedItems);
          calculateTotals(updatedItems);
        }
      } catch {
        // Pricing resolution is best-effort
      }
    };

    resolvePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.cliente_id]);

  // Anteprima live dei limiti sconto (venditore): avvisa prima del salvataggio
  // che sara' richiesta l'approvazione
  useEffect(() => {
    const hasSconti = items.some(it => (it.sconto_percentuale || 0) > 0);
    if (!hasSconti) {
      setScontoViolazioni([]);
      return;
    }
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const res = await offersAPI.validateSconti({
          items: items.map(it => ({
            descrizione: it.descrizione,
            sconto_percentuale: it.sconto_percentuale || 0,
            prodotto_id: it.prodotto_id,
          })),
        });
        if (active) setScontoViolazioni(res.data.violazioni || []);
      } catch {
        // Anteprima best-effort: il controllo vero avviene al salvataggio
      }
    }, 400);
    return () => {active = false; clearTimeout(timer);};
  }, [items, formData.cliente_id]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateOffertaRequest) => offersAPI.createOffer(data),
    onSuccess: (response) => {
      toast.success('Offerta creata con successo!');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['client-offers'] });
      const newId = response.data?.id;
      // Stay on the offer, switching to edit mode
      if (newId) {
        navigate(`/offers/${newId}/edit${returnTo !== '/offers' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`, { replace: true });
      } else {
        navigate(returnTo);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Errore durante la creazione');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CreateOffertaRequest) => offersAPI.updateOffer(id!, data),
    onSuccess: response => {
      savedDocument.current = null;
      queryClient.setQueryData(['offer',id],response);
      queryClient.invalidateQueries({queryKey:['offer-document-editor',id]});
      toast.success('Offerta aggiornata con successo!');
      // Stay in the editor instead of going back to the list
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['client-offers'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Errore durante l\'aggiornamento');
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const commercialUnsavedChanges = !!savedDocument.current && (
    savedDocument.current.form !== JSON.stringify(formData) || savedDocument.current.items !== JSON.stringify(items)
  );

  const hasUnsavedChanges = commercialUnsavedChanges || documentDirty;

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Shared shape for the three state-transition mutations below: call the API,
  // toast, and refresh the offer on success; toast the backend's error detail
  // (or a fallback) on failure.
  const useOfferTransitionMutation = <TVars,>(
    mutationFn: (vars: TVars) => Promise<any>,
    successMessage: string,
    errorFallback: string,
    onSuccessExtra?: () => void,
  ) => useMutation({
    mutationFn,
    onSuccess: () => {
      toast.success(successMessage);
      onSuccessExtra?.();
      queryClient.invalidateQueries({ queryKey: ['offer', id] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['client-offers'] });
      queryClient.invalidateQueries({ queryKey: ['offer-revisions', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || errorFallback);
    },
  });

  const requestValidationMutation = useOfferTransitionMutation<void>(
    () => offersAPI.requestValidation(id!), 'Validazione richiesta', 'Impossibile richiedere la validazione'
  );
  const validateOfferMutation = useOfferTransitionMutation<void>(
    () => offersAPI.validateOffer(id!), 'Offerta validata', 'Impossibile validare l’offerta'
  );
  const approveDiscountMutation = useOfferTransitionMutation<void>(
    () => offersAPI.approveDiscount(id!), 'Sconti approvati', 'Impossibile approvare gli sconti'
  );
  const sendOfferMutation = useOfferTransitionMutation<void>(
    () => offersAPI.sendOffer(id!), 'Offerta inviata', 'Errore durante l\'invio dell\'offerta'
  );
  const acceptOfferMutation = useOfferTransitionMutation<void>(
    () => offersAPI.acceptOffer(id!), 'Offerta accettata', 'Errore durante l\'accettazione dell\'offerta'
  );
  const rejectOfferMutation = useOfferTransitionMutation<string>(
    (note) => offersAPI.rejectOffer(id!, note || undefined),
    'Offerta rifiutata',
    'Errore durante il rifiuto dell\'offerta',
    () => { setShowRejectModal(false); setRejectNote(''); }
  );

  const deleteOfferMutation = useMutation({
    mutationFn: () => offersAPI.deleteOffer(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['client-offers'] });
      toast.success('Offerta eliminata');
      navigate(returnTo);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore durante l'eliminazione");
    },
  });

  const workflowBusy = isSaving || documentBusy || requestValidationMutation.isPending || validateOfferMutation.isPending || approveDiscountMutation.isPending || sendOfferMutation.isPending || acceptOfferMutation.isPending || rejectOfferMutation.isPending || deleteOfferMutation.isPending;
  const discardChanges = () => {
    const baseline = savedDocument.current;
    if (!baseline || workflowBusy || !hasUnsavedChanges) return;
    const restored = JSON.parse(baseline.form) as CreateOffertaRequest;
    pricingSequence.current += 1; pricingClient.current = restored.cliente_id;
    setFormData(restored); setItems(JSON.parse(baseline.items)); setItemPricing(JSON.parse(JSON.stringify(baseline.pricing)));
    setScontoViolazioni([]); setDragIndex(null); setDragOverIndex(null);
    documentDraft.current = null; setDocumentDirty(false); setDocumentReset(value => value + 1);
  };

  // Shared submit logic: used by the form and by the floating totals bar
  // (which lives outside the <form>, so browser validation is re-done manually)
  const submitOffer = () => {
    if (!canEditOffer || workflowBusy) return;
    if (documentDraft.current?.templateChanged) {toast.error('Applica prima il template in Contenuto documento'); setActiveTab('content'); return;}
    if (!formData.cliente_id) {
      toast.error('Seleziona un cliente');
      setActiveTab('details');
      return;
    }
    if (!formData.titolo.trim()) {
      toast.error('Inserisci il titolo dell\'offerta');
      setActiveTab('details');
      return;
    }

    const data = {
      ...formData,
      ...(documentDraft.current ? {section_values:documentDraft.current.section_values,template_id:documentDraft.current.template_id || formData.template_id} : {}),
      numero_custom: formData.numero_custom?.trim() || null,
      items: items,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitOffer();
  };

  // Flag set when a new item is added: the row would end up below the fold,
  // hidden behind the floating totals bar, so we scroll it into view
  const scrollToNewItem = useRef(false);

  useEffect(() => {
    if (!scrollToNewItem.current) return;
    scrollToNewItem.current = false;
    const rows = document.querySelectorAll('[data-offer-item-row]');
    const lastRow = rows[rows.length - 1] as HTMLElement | undefined;
    if (lastRow) {
      lastRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      lastRow.querySelector('input')?.focus();
    }
  }, [items]);

  const addItem = () => {
    const newItem: CreateOffertaItemRequest = {
      descrizione: '',
      quantita: 1,
      unita_misura: '',
      prezzo_unitario: 0,
      sconto_percentuale: 0,
      aliquota_iva: formData.aliquota_iva,
      importo_totale: 0,
      ordine: items.length,
    };
    scrollToNewItem.current = true;
    setItems([...items, newItem]);
  };

  const updateItem = (index: number, field: keyof CreateOffertaItemRequest, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Calculate total for this item
    const item = updatedItems[index];
    const subtotal = item.quantita * item.prezzo_unitario;
    const discount = subtotal * ((item.sconto_percentuale || 0) / 100);
    item.importo_totale = subtotal - discount;

    setItems(updatedItems);
    calculateTotals(updatedItems);
  };

  const removeItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    calculateTotals(updatedItems);
  };

  // Move an item from one position to another (drag & drop reorder)
  const moveItem = (from: number, to: number) => {
    if (from === to || from < 0 || from >= items.length || to < 0 || to >= items.length) return;

    const updatedItems = [...items];
    const [moved] = updatedItems.splice(from, 1);
    updatedItems.splice(to, 0, moved);
    // Keep ordine aligned with the new positions
    setItems(updatedItems.map((item, idx) => ({ ...item, ordine: idx })));

    // Pricing info is keyed by row index: remap it so it follows the rows
    const pricingArr = items.map((_, idx) => itemPricing[idx]);
    const [movedPricing] = pricingArr.splice(from, 1);
    pricingArr.splice(to, 0, movedPricing);
    const newPricing: Record<number, PriceResolution> = {};
    pricingArr.forEach((p, idx) => {
      if (p) newPricing[idx] = p;
    });
    setItemPricing(newPricing);
  };

  const resetDrag = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // IVA calcolata per singolo articolo: ogni riga usa la propria aliquota,
  // aliquotaIva e' il default per le righe che non ce l'hanno
  const calculateTotals = (items: CreateOffertaItemRequest[], aliquotaIva: number = formData.aliquota_iva) => {
    const imponibile = items.reduce((sum, item) => sum + Number(item.importo_totale || 0), 0);
    const iva = items.reduce((sum, item) => {
      const rate = item.aliquota_iva ?? aliquotaIva;
      return sum + Number(item.importo_totale || 0) * (rate / 100);
    }, 0);
    const totale = imponibile + iva;

    setFormData(prev => ({
      ...prev,
      importo_imponibile: imponibile,
      importo_iva: iva,
      importo_totale: totale,
    }));
  };

  if (isLoadingOffer) {
    return <div className="p-8 text-center">Caricamento...</div>;
  }

  return (
    <div className="space-y-4">
      <DocumentTypes current="offerta" />
      <button type="button" onClick={() => navigate(returnTo)} aria-label="Torna all’elenco" title="Torna all’elenco" className="inline-flex h-11 w-11 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-900"><ChevronLeftIcon aria-hidden="true" className="h-5 w-5" /></button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            {isEditing ? `Offerta ${offer?.numero || ''}` : 'Nuova offerta'}{revisions.current && <span className="ml-2 text-base font-medium text-gray-600">{' '}Rev {revisions.current.numero_revisione}</span>}
          </h1>
          {isEditing && offer?.scadenza && <p className="mt-2 text-sm text-gray-600">Scadenza {dateLabel(offer.scadenza)}</p>}
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-start justify-end gap-2">
          {isEditing && offer && <>
            <OfferStatusBadge stato={offer.stato} commerciale={offer.stato_commerciale} />
            <OfferExternalStatus offerId={offer.id} />
            {offer.stato === 'accettata' && hasPermission('offerte:create') && <button type="button" aria-label="Ordini e documenti" title="Ordini e documenti" disabled={workflowBusy || hasUnsavedChanges} onClick={() => navigate(`/sales-documents?offerta=${offer.id}${returnTo !== '/offers' ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`)} className="inline-flex h-11 w-11 items-center justify-center rounded border text-blue-700 disabled:opacity-50"><DocumentTextIcon aria-hidden="true" className="h-5 w-5" /></button>}
            {offer.stato === 'inviata' && canSave && <OfferRevisionCreate id={offer.id} numero={offer.numero} disabled={workflowBusy || hasUnsavedChanges} onCreated={() => setActiveTab('details')} />}
            {offer.stato === 'bozza' && <>
              {hasPermission('offerte:edit') && !offer.validazione_stato && <button type="button" aria-label="Richiedi validazione" title="Richiedi validazione" disabled={workflowBusy || hasUnsavedChanges} onClick={() => requestValidationMutation.mutate()} className="inline-flex h-11 w-11 items-center justify-center rounded border border-blue-300 text-blue-700 disabled:opacity-50"><ShieldCheckIcon aria-hidden="true" className="h-5 w-5" /></button>}
              {offer.approvazione_richiesta && offer.approvazione_stato === 'pending' && hasPermission('offerte:approve_discount') && <button type="button" aria-label="Approva sconti" title="Approva sconti" disabled={workflowBusy || hasUnsavedChanges} onClick={() => approveDiscountMutation.mutate()} className="inline-flex h-11 w-11 items-center justify-center rounded border border-green-300 text-green-700 disabled:opacity-50"><CheckCircleIcon aria-hidden="true" className="h-5 w-5" /></button>}
              {offer.validazione_stato === 'pending' && canApproveOffer && <button type="button" aria-label="Valida offerta" title="Valida offerta" disabled={workflowBusy || hasUnsavedChanges || (offer.approvazione_richiesta && offer.approvazione_stato !== 'approved')} onClick={() => validateOfferMutation.mutate()} className="inline-flex h-11 w-11 items-center justify-center rounded border border-green-300 text-green-700 disabled:opacity-50"><ShieldCheckIcon aria-hidden="true" className="h-5 w-5" /></button>}
              {canSendOfferState && <button type="button" aria-label="Invia offerta" title={offer.approvazione_richiesta && offer.approvazione_stato !== 'approved' ? 'Richiede approvazione sconti prima dell’invio' : 'Invia offerta'} aria-busy={sendOfferMutation.isPending} disabled={workflowBusy || hasUnsavedChanges || offer.validazione_stato !== 'approved' || (offer.approvazione_richiesta && offer.approvazione_stato !== 'approved')} onClick={() => sendOfferMutation.mutate()} className="inline-flex h-11 w-11 items-center justify-center rounded bg-blue-600 text-white disabled:opacity-50"><>{sendOfferMutation.isPending ? <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin motion-reduce:animate-none" /> : <PaperAirplaneIcon aria-hidden="true" className="h-5 w-5" />}</></button>}
              {canDeleteOffer && <button type="button" aria-label="Elimina" title="Elimina offerta" disabled={workflowBusy} onClick={() => setShowDeleteModal(true)} className="inline-flex h-11 w-11 items-center justify-center rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"><TrashIcon aria-hidden="true" className="h-5 w-5" /></button>}
            </>}
            {offer.stato === 'inviata' && canApproveOffer && <>
              <button type="button" aria-label="Accetta" title="Accetta offerta" aria-busy={acceptOfferMutation.isPending} disabled={workflowBusy || hasUnsavedChanges} onClick={() => acceptOfferMutation.mutate()} className="inline-flex h-11 w-11 items-center justify-center rounded bg-green-600 text-white disabled:opacity-50"><>{acceptOfferMutation.isPending ? <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin motion-reduce:animate-none" /> : <CheckCircleIcon aria-hidden="true" className="h-5 w-5" />}</></button>
              <button type="button" aria-label="Rifiuta" title="Rifiuta offerta" disabled={workflowBusy || hasUnsavedChanges} onClick={() => setShowRejectModal(true)} className="inline-flex h-11 w-11 items-center justify-center rounded bg-red-600 text-white disabled:opacity-50"><XCircleIcon aria-hidden="true" className="h-5 w-5" /></button>
            </>}
          </>}
          {canEditOffer && <button type="button" aria-label={isEditing ? 'Aggiorna Offerta' : 'Crea Offerta'} title="Salva tutte le modifiche dell’offerta" aria-busy={isSaving} disabled={workflowBusy || !hasUnsavedChanges} onClick={submitOffer} className="inline-flex h-11 w-11 items-center justify-center rounded bg-primary-600 text-white disabled:opacity-50"><>{isSaving ? <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin motion-reduce:animate-none" /> : <Save aria-hidden="true" className="h-5 w-5" />}</></button>}
          <button type="button" aria-label="Annulla modifiche" title="Ripristina tutte le modifiche non salvate" disabled={workflowBusy || !hasUnsavedChanges} onClick={discardChanges} className="inline-flex h-11 w-11 items-center justify-center rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"><Undo2 aria-hidden="true" className="h-5 w-5" /></button>
        </div>
      </div>

      <fieldset disabled={!canEditOffer || isSaving} className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-4 grid min-w-0 max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0">
          <div className="relative mb-1 flex items-center gap-2">
            <label htmlFor="offer-number" className="text-sm font-medium text-gray-700">Numero offerta</label>
            <details>
              <summary aria-label="Informazioni sul numero offerta" title="Informazioni sul numero offerta" className="flex h-7 w-7 cursor-pointer list-none items-center justify-center rounded-full border border-gray-300 font-serif text-sm font-semibold text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 [&::-webkit-details-marker]:hidden">i</summary>
              <p id="offer-number-help" className="absolute left-0 top-full z-20 mt-1 w-72 max-w-full rounded-lg border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-600 shadow-lg">Se l’offerta ha già un numero, inseriscilo esattamente come nel documento condiviso. Lascia vuoto solo per una nuova numerazione progressiva.</p>
            </details>
          </div>
          <input id="offer-number" type="text" maxLength={50}
            value={formData.numero_custom || ''}
            onChange={event => setFormData(previous => ({ ...previous, numero_custom: event.target.value }))}
            disabled={isEditing && (offer?.stato !== 'bozza' || !!revisions.current)}
            placeholder="Automatico, se non ancora assegnato"
            aria-describedby="offer-number-help"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
          </div>
          <div className="min-w-0">
            <label htmlFor="offer-date" className="mb-1 flex h-7 items-center text-sm font-medium text-gray-700">Data offerta</label>
            <input id="offer-date" type="text" readOnly value={offer ? dateLabel(offer.created_at) : 'Assegnata al salvataggio'} className="block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700" />
          </div>
        </div>
        <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="offer-client" className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente *
                </label>
                <ClientPicker
                  value={formData.cliente_id}
                  selectedClient={selectedClient}
                  onChange={(client) => setFormData({ ...formData, cliente_id: client.id })}
                  disabled={isEditing && offer?.stato !== 'bozza'}
                />
                {isEditing && offer?.stato !== 'bozza' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Il cliente non può essere modificato dopo l'invio dell'offerta.
                  </p>
                )}
              </div>
            <div className="min-w-0">
              <label htmlFor="offer-title" className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                id="offer-title"
                type="text"
                value={formData.titolo}
                onChange={(e) => setFormData({ ...formData, titolo: e.target.value })}
                required
                className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Inserisci il titolo dell'offerta"
              />
            </div>
        </div>
        <div className="mt-3 empty:hidden">
              {/* Riepilogo del cliente selezionato */}
              {selectedClient && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  <span className="font-medium text-gray-900">
                    {selectedClient.ragione_sociale || `${selectedClient.nome} ${selectedClient.cognome || ''}`.trim()}
                  </span>
                  {selectedClient.partita_iva && <span>P.IVA {selectedClient.partita_iva}</span>}
                  {selectedClient.codice_fiscale && <span>CF {selectedClient.codice_fiscale}</span>}
                  {selectedClient.email && <span>{selectedClient.email}</span>}
                  {selectedClient.telefono && <span>Tel. {selectedClient.telefono}</span>}
                  {(selectedClient.indirizzo || selectedClient.citta) && (
                    <span>
                      {[selectedClient.indirizzo, selectedClient.cap, selectedClient.citta, selectedClient.provincia]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  )}
                </div>
              )}
        </div>
      </fieldset>

      {/* Tab Navigation */}
      <div ref={tabsRef} className="mb-4 rounded-lg border border-gray-300 bg-white p-1">
        <nav aria-label="Sezioni dell’offerta" className="flex gap-1 overflow-x-auto whitespace-nowrap">
          {([
            { key: 'details', label: 'Dettagli Offerta', Icon: Cog6ToothIcon },
            { key: 'content', label: 'Contenuto Documento', Icon: DocumentTextIcon },
            { key: 'attivita', label: 'Attività', Icon: CalendarDaysIcon },
            { key: 'revisions', label: 'Revisioni', Icon: History },
          ] as const).map(({ key, label, Icon }) => <button
            key={key}
            type="button"
            aria-current={activeTab === key ? 'page' : undefined}
            onClick={() => setActiveTab(key)}
            className={`inline-flex min-h-11 shrink-0 items-center rounded-md px-4 py-2.5 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 ${activeTab === key ? 'bg-blue-800 text-white shadow-sm' : 'text-gray-800 hover:bg-gray-100'}`}
          >
            <Icon aria-hidden="true" className="mr-2 h-4 w-4" />
            {label}
          </button>)}
        </nav>
      </div>

      {/* Approval Warning Banner */}
      {isEditing && offerData?.data?.approvazione_richiesta && (
        <div className={`rounded-md p-3 mb-4 ${
          offerData.data.approvazione_stato === 'pending'
            ? 'bg-yellow-50 border border-yellow-200'
            : offerData.data.approvazione_stato === 'approved'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {offerData.data.approvazione_stato === 'pending' && (
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              )}
              {offerData.data.approvazione_stato === 'approved' && (
                <ShieldCheckIcon className="h-5 w-5 text-green-400" />
              )}
              {offerData.data.approvazione_stato === 'rejected' && (
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                offerData.data.approvazione_stato === 'pending'
                  ? 'text-yellow-800'
                  : offerData.data.approvazione_stato === 'approved'
                  ? 'text-green-800'
                  : 'text-red-800'
              }`}>
                {offerData.data.approvazione_stato === 'pending' && 'In attesa di approvazione sconti'}
                {offerData.data.approvazione_stato === 'approved' && 'Sconti approvati'}
                {offerData.data.approvazione_stato === 'rejected' && 'Sconti rifiutati'}
              </h3>
              <div className={`mt-1 text-sm ${
                offerData.data.approvazione_stato === 'pending'
                  ? 'text-yellow-700'
                  : offerData.data.approvazione_stato === 'approved'
                  ? 'text-green-700'
                  : 'text-red-700'
              }`}>
                {offerData.data.approvazione_stato === 'pending' && (
                  <p>Questa offerta contiene sconti che superano il limite autorizzato. Non può essere inviata fino all'approvazione.</p>
                )}
                {offerData.data.approvazione_stato === 'approved' && (
                  <p>Gli sconti applicati sono stati approvati. L'offerta può essere inviata.</p>
                )}
                {offerData.data.approvazione_stato === 'rejected' && (
                  <>
                    <p>Gli sconti applicati sono stati rifiutati. Modifica gli sconti e riprova.</p>
                    {offerData.data.approvazione_note && (
                      <p className="mt-1 font-medium">Motivo: {offerData.data.approvazione_note}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WMS Order Section */}
      {isEditing && wmsStatus?.connected && isAccettata && (
        <div className="bg-white shadow rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <CubeIcon className="h-5 w-5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900">Ordine Magazzino</h3>
          </div>

          {!hasWmsOrder && (
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500">Nessun ordine magazzino associato.</p>
              <button
                onClick={() => id && createWmsOrder.mutate(id)}
                disabled={createWmsOrder.isPending}
                className="px-3 py-1.5 text-xs border border-blue-300 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {createWmsOrder.isPending ? 'Creazione...' : 'Crea Ordine Magazzino'}
              </button>
            </div>
          )}

          {hasWmsOrder && offer && (
            <div className="space-y-3">
              <WmsOrderTracker
                wmsOrderStatus={offer.wms_order_status || 'DRAFT'}
                statoEvasione={offer.stato_evasione || 'non_evaso'}
                wmsOrderNumber={offer.wms_order_number || ''}
              />
              <div className="flex gap-2 pt-2">
                {offer.wms_order_status === 'DRAFT' && (
                  <button
                    onClick={() => id && confirmWmsOrder.mutate(id)}
                    disabled={confirmWmsOrder.isPending}
                    className="px-3 py-1.5 text-xs border border-green-300 rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {confirmWmsOrder.isPending ? 'Conferma...' : 'Conferma Ordine'}
                  </button>
                )}
                <button
                  onClick={() => id && syncWmsStatus.mutate(id)}
                  disabled={syncWmsStatus.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`h-3 w-3 ${syncWmsStatus.isPending ? 'animate-spin' : ''}`} />
                  Aggiorna Stato
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details Tab */}
      {activeTab === 'details' && (
        <form onSubmit={handleSubmit} className={`grid min-w-0 grid-cols-1 items-start gap-4 ${configurationOpen ? 'lg:grid-cols-[minmax(0,1fr)_22rem]' : 'lg:grid-cols-[minmax(0,1fr)_2.5rem]'}`}>
          <fieldset disabled={!canEditOffer || isSaving} className="order-2 min-w-0 space-y-4 lg:order-1">
          {/* Items */}
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-base font-medium text-gray-900">Articoli</h2>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Aggiungi Articolo
              </button>
            </div>

            {/* Avviso preventivo: sconti oltre il limite dell'utente */}
            {scontoViolazioni.length > 0 && (
              <div className="mb-3 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-800">
                  {scontoViolazioni.length === 1
                    ? 'Una riga supera il tuo limite di sconto'
                    : `${scontoViolazioni.length} righe superano il tuo limite di sconto`}
                  : al salvataggio l'offerta richiederà l'approvazione di un responsabile prima di poter essere inviata.
                </p>
              </div>
            )}

            {items.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <PlusIcon className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                <p className="text-sm">Nessun articolo aggiunto</p>
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-2 text-primary-600 hover:text-primary-700"
                >
                  Aggiungi il primo articolo
                </button>
              </div>
            ) : (
              <div role="region" aria-label="Elenco articoli" tabIndex={0} className="max-h-[60dvh] overflow-auto overscroll-contain">
                {/* Table rows */}
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                  {items.map((item, index) => {
                    const pricing = itemPricing[index];
                    const hasPromos = pricing?.promozioni_applicate && pricing.promozioni_applicate.length > 0;
                    const listino = pricing ? Number(pricing.prezzo_listino ?? pricing.prezzo_base) : null;
                    const violazione = scontoViolazioni.find(v => v.item_index === index);

                    return (
                      <div
                        key={index}
                        data-offer-item-row
                        onDragOver={(e) => {
                          if (dragIndex === null || dragIndex === index) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDragOverIndex(index);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragIndex !== null) moveItem(dragIndex, index);
                          resetDrag();
                        }}
                        className={`${hasPromos ? 'bg-green-50/30' : ''} ${dragIndex === index ? 'opacity-40' : ''} ${
                          dragOverIndex === index && dragIndex !== null && dragIndex !== index
                            ? 'shadow-[inset_0_2px_0_0_#2563eb]'
                            : ''
                        }`}
                      >
                        <div className="px-3 py-3">
                          <div className="flex items-center gap-2">
                          {/* Handle drag & drop */}
                          <div className="flex shrink-0 justify-center">
                            <button
                              type="button"
                              draggable
                              onDragStart={(e) => {
                                setDragIndex(index);
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('text/plain', String(index));
                                const row = (e.currentTarget as HTMLElement).closest('[data-offer-item-row]');
                                if (row) e.dataTransfer.setDragImage(row as HTMLElement, 16, 16);
                              }}
                              onDragEnd={resetDrag}
                              className="p-2 text-gray-400 hover:text-gray-500 cursor-grab active:cursor-grabbing select-none"
                              title="Trascina per riordinare"
                            >
                              <Bars3Icon className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Descrizione */}
                          <div className="min-w-0 flex-1 [&_input]:font-medium">
                            <span className="sr-only">Descrizione articolo {index + 1}</span>
                            <ProductAutocomplete
                              value={item.descrizione}
                              onChange={(val) => updateItem(index, 'descrizione', val)}
                              clienteId={formData.cliente_id || undefined}
                              onProductSelect={(sel: ProductSelection) => {
                                const updated = [...items];
                                updated[index] = {
                                  ...updated[index],
                                  descrizione: sel.descrizione,
                                  prezzo_unitario: sel.prezzo_unitario,
                                  unita_misura: sel.unita_misura,
                                  aliquota_iva: sel.aliquota_iva ?? updated[index].aliquota_iva ?? formData.aliquota_iva,
                                  prodotto_id: sel.prodotto_id,
                                };
                                const subtotal = updated[index].quantita * sel.prezzo_unitario;
                                const discount = subtotal * ((updated[index].sconto_percentuale || 0) / 100);
                                updated[index].importo_totale = subtotal - discount;
                                setItems(updated);
                                calculateTotals(updated);
                                if (sel.pricing) {
                                  setItemPricing(prev => ({ ...prev, [index]: sel.pricing! }));
                                }
                              }}
                              required
                            />
                          </div>

                          {/* Elimina */}
                          <div className="flex shrink-0 justify-center">
                            <button
                              type="button"
                              aria-label={`Rimuovi articolo ${index + 1}`}
                              title="Rimuovi articolo"
                              onClick={() => removeItem(index)}
                              className="p-2 text-gray-400 hover:bg-red-50 rounded hover:text-red-600 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          </div>
                          {pricing?.prodotto_nome && pricing.prodotto_nome !== item.descrizione && (
                            <p className="ml-10 mt-1 text-xs text-gray-500">{pricing.prodotto_nome}</p>
                          )}
                          <div className="mt-3 grid grid-cols-2 items-end gap-3 sm:grid-cols-5 tabular-nums">
                          {/* Quantità */}
                          <div>
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Quantità</span>
                            <div className="relative">
                            <input
                              type="number"
                              min="1"
                              step="0.01"
                              aria-label={`Quantità articolo ${index + 1}`}
                              value={item.quantita}
                              onChange={(e) => updateItem(index, 'quantita', parseFloat(e.target.value) || 0)}
                              required
                              className="block w-full pl-2 pr-9 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            />
                            <span className="pointer-events-none absolute right-2 top-1.5 text-sm text-gray-500">{item.unita_misura || '—'}</span>
                            </div>
                          </div>

                          {/* Prezzo Unitario (read-only, shows promo price) */}
                          <div className="text-right">
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Prezzo unit.</span>
                            <span className={`flex h-[34px] items-center justify-end text-sm font-medium ${hasPromos ? 'text-green-700' : 'text-gray-900'}`}>
                              €{Number(item.prezzo_unitario).toFixed(2)}
                            </span>
                          </div>

                          {/* Sconto % */}
                          <div>
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Sconto %</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              aria-label={`Sconto articolo ${index + 1}`}
                              value={item.sconto_percentuale}
                              onChange={(e) => updateItem(index, 'sconto_percentuale', parseFloat(e.target.value) || 0)}
                              className={`block w-full px-2 py-1.5 text-sm text-right border rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                                violazione || (isEditing && offerData?.data?.approvazione_richiesta && offerData?.data?.approvazione_stato === 'pending' && (item.sconto_percentuale || 0) > 0)
                                  ? 'border-yellow-400 bg-yellow-50'
                                  : 'border-gray-300'
                              }`}
                            />
                          </div>

                          <div className="text-right">
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Netto unit.</span>
                            <span className="flex h-[34px] items-center justify-end text-sm text-gray-900">€{(Number(item.prezzo_unitario) * (1 - Number(item.sconto_percentuale || 0) / 100)).toFixed(2)}</span>
                          </div>
                          {/* Totale riga */}
                          <div className="text-right font-semibold text-base text-gray-900">
                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">Totale riga</span>
                            <span className="flex h-[34px] items-center justify-end">€{Number(item.importo_totale || 0).toFixed(2)}</span>
                          </div>

                          </div>
                          <div className="mt-2 flex flex-wrap justify-end gap-x-4 gap-y-1 text-[11px] text-gray-500">
                            {listino != null && <span>Listino €{listino.toFixed(2)}</span>}
                            <span>IVA {Number(item.aliquota_iva ?? formData.aliquota_iva)}% esclusa</span>
                          </div>
                        </div>

                        {/* Sub-row: promozioni + avviso limite sconto */}
                        {(hasPromos || violazione) && (
                          <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
                            {hasPromos && pricing!.promozioni_applicate.map((p) => (
                              <span
                                key={p.promozione_id}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                                  p.impatta_prezzo === false
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-green-100 text-green-700 border-green-200'
                                }`}
                              >
                                {p.nome}
                                {p.impatta_prezzo === false ? (
                                  <span className="font-medium">Solo tracciamento</span>
                                ) : (
                                  <>
                                    <span className="font-semibold">
                                      {p.tipo === 'sconto_percentuale' ? `-${Number(p.valore)}%` : `€${Number(p.valore).toFixed(2)}`}
                                    </span>
                                    <span className="text-green-500">
                                      €{Number(p.prezzo_prima).toFixed(2)} → €{Number(p.prezzo_dopo).toFixed(2)}
                                    </span>
                                  </>
                                )}
                              </span>
                            ))}
                            {violazione && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800 border border-yellow-200">
                                Sconto {Number(violazione.sconto_richiesto)}% oltre il tuo limite (max {Number(violazione.sconto_max)}%): richiede approvazione
                              </span>
                            )}
                            {!violazione && isEditing && offerData?.data?.approvazione_richiesta && offerData?.data?.approvazione_stato !== 'approved' && (item.sconto_percentuale || 0) > 0 && (
                              <span className="text-xs text-yellow-600 ml-1">Sconto oltre il limite</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          </fieldset>
          <div ref={configurationRef} aria-label="Configurazione offerta" className="order-1 flex min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white lg:order-2 lg:sticky lg:top-4"
            onKeyDown={(event) => {
              if (event.key === 'Escape' && !configurationPinned) setConfigurationOpen(false);
            }}>
            <div hidden={!configurationOpen} className="min-w-0 flex-1">
              <div className="flex h-12 items-center justify-between border-b border-gray-200 bg-gray-50 px-4">
                <h2 className="text-sm font-semibold text-gray-900">Configurazione offerta</h2>
                <button type="button" aria-pressed={configurationPinned}
                  aria-label={configurationPinned ? 'Sblocca pannello (unpin)' : 'Fissa pannello (pin)'}
                  title={configurationPinned ? 'Sblocca: si chiude cliccando fuori' : 'Fissa: mantieni aperto'}
                  onClick={() => setConfigurationPinned(!configurationPinned)}
                  className={`rounded p-2 hover:bg-gray-200 ${configurationPinned ? 'text-primary-700' : 'text-gray-500'}`}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill={configurationPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6l-1 6 4 4v2h-5v6l-1 1-1-1v-6H6v-2l4-4-1-6Z" />
                  </svg>
                </button>
              </div>
            <fieldset disabled={!canEditOffer || isSaving} id="offer-configuration-panel" className="max-h-[calc(100dvh-12rem)] overflow-y-auto divide-y divide-gray-200 [&>div]:rounded-none [&>div]:border-0 [&>aside]:rounded-none [&>aside]:border-0">
          {/* Client and Basic Info */}
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-base font-medium text-gray-900 mb-3">Informazioni Generali</h2>

            <p className="text-sm text-gray-500">Le impostazioni di stampa sono in Contenuto documento.</p>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione
              </label>
              <textarea
                value={formData.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                rows={2}
                className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Inserisci una descrizione dettagliata"
              />
            </div>
          </div>

          <OfferPromotionsPanel
            promotions={selectablePromozioni}
            selectedIds={formData.promozione_ids || []}
            automaticPromotions={items.flatMap((_, index) => itemPricing[index]?.promozioni_applicate || [])}
            onChange={(promozione_ids) => setFormData((current) => ({ ...current, promozione_ids }))}
            disabled={isEditing && offer?.stato !== 'bozza'}
            loading={promotionsLoading}
            error={promotionsError}
            onRetry={() => refetchPromotions()}
          />

          {/* Validity (totals live in the floating bar; IVA is per-item) */}
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-base font-medium text-gray-900 mb-3">Validità</h2>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Giorni di Validità
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.giorni_validita}
                  onChange={(e) => setFormData({ ...formData, giorni_validita: parseInt(e.target.value) || 30 })}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data di Scadenza
                </label>
                <input
                  type="date"
                  value={formData.scadenza}
                  onChange={(e) => setFormData({ ...formData, scadenza: e.target.value })}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Additional Settings */}
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-base font-medium text-gray-900 mb-3">Impostazioni Aggiuntive</h2>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condizioni di Pagamento
                </label>
                <textarea
                  value={formData.condizioni_pagamento}
                  onChange={(e) => setFormData({ ...formData, condizioni_pagamento: e.target.value })}
                  rows={2}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Inserisci le condizioni di pagamento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={2}
                  className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Inserisci note aggiuntive"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="firma_digitale"
                  checked={formData.firma_digitale}
                  onChange={(e) => setFormData({ ...formData, firma_digitale: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="firma_digitale" className="ml-2 block text-sm text-gray-900">
                  Richiedi firma digitale
                </label>
              </div>
            </div>
          </div>
          </fieldset>
          </div>
            <button type="button" aria-expanded={configurationOpen} aria-controls="offer-configuration-panel"
              aria-label={configurationOpen ? 'Comprimi configurazione' : 'Espandi configurazione'}
              onClick={() => setConfigurationOpen(!configurationOpen)}
              className="flex w-10 shrink-0 flex-col items-center gap-3 self-stretch border-l border-gray-200 bg-gray-50 py-3 text-gray-600 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500">
              {configurationOpen ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
              <span className="text-xs tracking-wide [writing-mode:vertical-rl]">Configurazione offerta</span>
            </button>
          </div>
        </form>
      )}

      <div hidden={activeTab !== 'content'}>
        {offer ? <OfferDocumentPanel key={`${offer.id}:${documentReset}`} offer={offer} templates={templates} blocked={commercialUnsavedChanges} parentSaving={isSaving} onDirtyChange={setDocumentDirty} onBusyChange={setDocumentBusy} onDraftChange={onDocumentDraft} onSaveAll={submitOffer} />
          : <div className="rounded border bg-white p-6">Salva prima l’offerta per scegliere il template e compilare il documento.</div>
        }
      </div>

      {/* Attività Tab - solo in modifica (offerta salvata) */}
      {activeTab === 'attivita' && (
        isEditing && id ? (
          <AttivitaTab context={{ kind: 'offerta', id }} />
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            <CalendarDaysIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Salva prima l'offerta per poter registrare delle attività.</p>
          </div>
        )
      )}

      {activeTab === 'revisions' && (offer
        ? <section aria-label="Storico revisioni" className="max-h-[calc(100dvh-20rem)] min-h-40 overflow-auto"><OfferRevisions id={offer.id} canRevise={false} showCurrent={false} /></section>
        : <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-700">Salva prima l’offerta per consultarne le revisioni.</div>
      )}

      {/* Pannello fluttuante: riepilogo totali sempre visibile + salvataggio */}
      {activeTab !== 'content' && activeTab !== 'revisions' && <div className="sticky bottom-4 z-30 flex justify-center pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-3 sm:gap-5 rounded-2xl border border-gray-200 bg-white/95 backdrop-blur-sm px-4 sm:px-5 py-2.5 shadow-[0_8px_30px_rgba(15,23,42,0.12)]">
          <div className="hidden md:flex flex-col items-center min-w-[56px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Articoli</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">{items.length}</span>
          </div>
          <div className="hidden md:block h-8 w-px bg-gray-200" />
          <div className="flex flex-col items-center min-w-[84px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Imponibile</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">€{formData.importo_imponibile.toFixed(2)}</span>
          </div>
          <div className="hidden sm:flex flex-col items-center min-w-[84px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">IVA</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">€{formData.importo_iva.toFixed(2)}</span>
          </div>
          <div className="h-8 w-px bg-gray-200" />
          <div className="flex flex-col items-center min-w-[96px]">
            <span className="text-[10px] font-medium uppercase tracking-wider text-primary-500">Totale</span>
            <span className="text-lg font-bold text-primary-600 tabular-nums leading-tight">€{formData.importo_totale.toFixed(2)}</span>
          </div>

        </div>
      </div>}

      {/* Reject Offer Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => { setShowRejectModal(false); setRejectNote(''); }} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Rifiuta offerta</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivazione (opzionale)
                </label>
                <textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  rows={3}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="Inserisci il motivo del rifiuto..."
                />
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => { setShowRejectModal(false); setRejectNote(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Annulla
                </button>
                <button
                  onClick={() => rejectOfferMutation.mutate(rejectNote)}
                  disabled={rejectOfferMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {rejectOfferMutation.isPending ? 'Rifiutando...' : 'Rifiuta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmModal
          isOpen={showDeleteModal}
          title="Elimina offerta"
          message={`Sei sicuro di voler eliminare l'offerta "${offer?.numero || ''} - ${offer?.titolo || ''}"? Questa operazione è reversibile (soft delete).`}
          confirmLabel="Elimina"
          onConfirm={() => deleteOfferMutation.mutate()}
          onClose={() => setShowDeleteModal(false)}
          isLoading={deleteOfferMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
};

export default OfferEditor;
